import type * as React from "react";

import { cx } from "../../lib/cx";
import { Icon, type IconName } from "./Icon";
import s from "./Button.module.css";

export type ButtonVariant = "primary" | "secondary" | "soft" | "ghost" | "outline" | "accent" | "inverse" | "ink" | "danger" | "danger-soft";
export type ButtonSize = "sm" | "md" | "lg";

const ICON_SIZE: Record<ButtonSize, number> = { sm: 15, md: 18, lg: 20 };

export interface ButtonProps extends Omit<React.ComponentPropsWithoutRef<"button">, "type"> {
  /** primary = violeta sólido; secondary = blanco con borde y texto violeta; soft = violeta sobre accent-50, sin
   *  borde (la acción de un estado vacío); ghost = sólo texto; outline = borde del color del texto (sobre accent o
   *  sobre oscuro); accent = cian sólido; inverse = blanco con texto violeta, para ir sobre el banner accent;
   *  ink = tinta sólida con texto blanco (descargar el cartel: una acción física, no de marca); danger = rojo. */
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Icono antes del texto. */
  icon?: IconName;
  /** Icono después del texto. */
  iconAfter?: IconName;
  /** La acción está en curso: spinner en lugar del icono, `aria-busy` y sin clics. El texto se queda. */
  pending?: boolean;
  fullWidth?: boolean;
  type?: "button" | "submit" | "reset";
  /** Con `href` el botón es un `<a>` (o el componente de `as`, p. ej. `Link` de Next). */
  href?: string;
  /** Qué elemento renderizar. Con `href`, por default `"a"`; sin `href`, `"button"`. */
  as?: React.ElementType;
  target?: string;
  rel?: string;
}

/** La acción de Klavia. El texto es un verbo en sentence case, sin punto final. */
export function Button({
  children,
  variant = "primary",
  size = "md",
  icon,
  iconAfter,
  pending = false,
  disabled = false,
  fullWidth = false,
  type = "button",
  href,
  as,
  target,
  rel,
  className,
  ...rest
}: ButtonProps) {
  const Tag: React.ElementType = as ?? (href ? "a" : "button");
  const esBoton = Tag === "button";
  const inactivo = disabled || pending;
  const iconSize = ICON_SIZE[size];

  // Un enlace no tiene `disabled`: se le saca el `href` para que no navegue y
  // se anuncia con `aria-disabled`. El `rel` de un `target="_blank"` se pone
  // solo, porque olvidarlo es la regla y no la excepción.
  const propsDelTag = esBoton
    ? { type, disabled: inactivo }
    : {
        href: inactivo ? undefined : href,
        target,
        rel: rel ?? (target === "_blank" ? "noopener noreferrer" : undefined),
        "aria-disabled": inactivo || undefined,
      };

  return (
    <Tag
      className={cx(s.button, className)}
      data-variant={variant}
      data-size={size}
      data-full-width={fullWidth || undefined}
      aria-busy={pending || undefined}
      {...propsDelTag}
      {...rest}
    >
      {pending ? (
        <Icon name="loader-2" size={iconSize} className={s.spinner} />
      ) : icon ? (
        <Icon name={icon} size={iconSize} />
      ) : null}
      {children}
      {iconAfter && !pending ? <Icon name={iconAfter} size={iconSize} /> : null}
    </Tag>
  );
}
